import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FUND_INVESTMENTS } from 'modules/fundInvestments/queries';
import { DeleteFundScenarioInvestmentScenarios } from 'modules/fundScenarioInvestmentScenarios/mutations';
import MaterialTable from 'material-table';
import { Button } from '@material-ui/core';
import materialTableIcons from 'assets/matrielTableIcons';

import { EeConfirmDialog } from 'pages/Scenarios/components/EeConfirmDialog';
import { DetailPanel } from './detailPanel';
import './style.scss';
import { ChangeFundInvestment } from 'modules/graphql';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import { toJS } from 'mobx';
import { useSnackbar } from 'hooks/useSnackbar';

const mapStatusToText = (statusCode: string) => {
  switch (statusCode) {
    case 'A_10':
      return 'active';
    case 'A_20':
      return 'inactive';
    case 'A_30':
      return 'inactive';
    case 'A_40':
      return 'inactive';

    default:
      return 'inactive';
  }
};

export const CollapsibleTable = observer((props: any) => {
  const { fundId, fundName, columns } = props;

  const { fundScenarioStore } = useStores();

  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [resetConfirmIsOpen, setResetConfirmIsOpen] = useState(false);

  const { addSnackbar } = useSnackbar();

  // For the table
  const { loading, error, data } = useQuery(FUND_INVESTMENTS, {
    variables: { id: fundId },
  });

  // For mobx
  const { refetch: refetchInvestments } = useQuery(FUND_INVESTMENTS, {
    variables: { id: fundId },
  });

  const [changeFundInvestment] = useMutation(ChangeFundInvestment);
  const [deleteFundInvestmentsScenarios] = useMutation(
    DeleteFundScenarioInvestmentScenarios,
  );

  const tableRef = useRef<any>(null);

  useEffect(() => {
    if (data) {
      const mapDataToColumns = data.fundInvestments?.map((f: any) => {
        return {
          fundId: fundId,
          investmentId: f.id,
          status: f.status,
          scenarios: f.scenarios,
          statusText: mapStatusToText(f.status),
          company: f.company,
          captable: f.company.documentId,
          captableUrl: f.capTableUrl,
          investedCapital: f.investedCapital,
          ownershipPercent: f.ownershipPercent
            ? (Math.round(f.ownershipPercent * 100) / 100).toFixed(2)
            : f.ownershipPercent,
          nav: Math.round(f.nav),
          ev: f.ev ? Math.round(f.ev) : f.ev,
          multiple: f.multiple
            ? (Math.round(f.multiple * 100) / 100).toFixed(2)
            : f.multiple,
          irr: f.irr ? (Math.round(f.irr * 10000) / 100).toFixed(2) : f.irr,
          tableData: { checked: f.includeInScenariosCalcs },
        };
      });

      if (error) {
        console.error('Failed to get fund investments', error);
        displayError();
      }

      setRows(mapDataToColumns);
      setSelectedRows(
        mapDataToColumns
          .filter((r: any) => r.tableData.checked)
          .map((r: any) => r.investmentId),
      );
    }
  }, [data, error, fundId]);

  // Refresh data in case that parent made changes in scenarios
  useEffect(() => {
    refetchInvestments().then((response) => {
      fundScenarioStore.setFundInvestments(response.data.fundInvestments);
      // turn off the spinners
      const isCalculating = response.data.fundInvestments.some(
        (a: any) => a.calculationStatus === 'A_20' && a.includeInScenariosCalcs,
      );
      if (!isCalculating) {
        fundScenarioStore.setIsCalculated(true);
      }
    });
  }, [props.scenarios]);

  useEffect(() => {
    closeAllRows();
  }, [props.onScenarioChange]);

  function displayError() {
    addSnackbar('Failed to get fund investments');
  }

  async function changeFundInvestmentSelection(gridSelectedRows: any[]) {
    let changed;
    if (
      selectedRows.length > gridSelectedRows.length ||
      gridSelectedRows.length === 0
    ) {
      // uncheck
      changed = selectedRows
        .filter((s) => !gridSelectedRows.some((r) => r.investmentId === s))
        .map((investmentId) => {
          return {
            includeInScenariosCalcs: false,
            id: investmentId,
          };
        });
    } else {
      // check
      changed = gridSelectedRows
        .filter((s) => selectedRows.indexOf(s.investmentId) === -1)
        .map((r) => {
          return {
            includeInScenariosCalcs: true,
            id: r.investmentId,
          };
        });
    }

    fundScenarioStore.setIsCalculated(false);
    setSelectedRows(gridSelectedRows.map((r) => r.investmentId));

    await changeFundInvestment({
      variables: {
        fundInvestments: changed,
      },
    });
  }

  function closeAllRows() {
    rows.forEach((r, i) => {
      tableRef.current.onToggleDetailPanel([i], () => {});
    });
  }

  async function resetAll() {
    const ids = toJS(fundScenarioStore.fundInvestments).map((i) => {
      return i.scenarios
        .filter((s: any) => s.id !== null)
        .map((s: any) => s.id);
    });
    const allIds = ids.flat();
    if (allIds && allIds.length > 0) {
      await deleteFundInvestmentsScenarios({
        variables: {
          scenarios: allIds,
        },
      });
      // Refresh the data
      closeAllRows();
      fundScenarioStore.isCalculated = false;
    }
  }

  return (
    <div className="bg-white MuiPaper-elevation2 p-4 position-relative lens-section lens-table">
      <EeConfirmDialog
        open={resetConfirmIsOpen}
        confirmButtonLabel="Reset All"
        onCancel={() => setResetConfirmIsOpen(false)}
        onConfirm={() => {
          resetAll().then(() => {});
          setResetConfirmIsOpen(false);
        }}
      >
        <p className="fs-16 mb-0">Reset All Companies?</p>
        <p className="fs-14 mb-0">
          This will reset all of this fund’s company scenarios to the default
          and actual company parameters.
          <br /> All of this fund’s updated scenario data will be lost. Are you
          sure?
        </p>
      </EeConfirmDialog>
      <Button
        color="primary"
        size="small"
        variant="outlined"
        className="position-absolute mr-4 fs-16 text-capitalize"
        style={{
          right: 0,
          top: 0,
          marginTop: 32,
          zIndex: 1,
          padding: '5px 20px',
        }}
        onClick={() => setResetConfirmIsOpen(true)}
      >
        Reset All
      </Button>
      {/*@ts-ignore*/}
      <MaterialTable
        style={{ boxShadow: 'none', zIndex: 0 }}
        tableRef={tableRef}
        className="px-4 mx-5 lens-table"
        // other props
        // @ts-ignore
        icons={materialTableIcons}
        isLoading={loading}
        options={{
          paging: false,
          searchFieldVariant: 'outlined',
          searchFieldStyle: { height: '36px', fontSize: 14 },
          selection: true,
          search: true,
          searchFieldAlignment: 'left',
          headerStyle: {
            background: '#e9ecef',
            height: '20px',
            fontSize: '16px',
            fontWeight: '600',
            fontStretch: 'normal',
            fontStyle: 'normal',
            lineHeight: '1.25',
            letterSpacing: 'normal',
            color: '#6a757a',
          },
        }}
        title="Portfolio"
        columns={columns}
        data={rows}
        onRowClick={(event, rowData, togglePanel) => {
          // @ts-ignore
          if (!event?.target.href && event?.target.alt !== 'Captable') {
            // @ts-ignore
            togglePanel();
          }
        }}
        onSelectionChange={(selected) => {
          changeFundInvestmentSelection(selected).then();
        }}
        detailPanel={[
          {
            render: (rowData) => {
              return (
                <DetailPanel
                  fundName={fundName}
                  rowData={rowData}
                  closeAllRows={closeAllRows}
                  fundCurrencySign={props.fundCurrencySign}
                />
              );
            },
          },
        ]}
      />
    </div>
  );
});
